/**
 * 三份量表的題目文字、選項標籤與構面對應。
 * 與 Flutter app/lib/data/questionnaires/questionnaire_data.dart 同步。
 */

export interface QuestionDef {
  number: number
  text: string
  /** 該題對應的構面 key，用於分組顯示 */
  dimensionKey: string
}

/** career_calling: question.number → dimensionKey */
const CAREER_CALLING_DIM_MAP: Record<number, string> = {
  // interest_passion
  2: 'interest_passion',
  3: 'interest_passion',
  7: 'interest_passion',
  8: 'interest_passion',
  11: 'interest_passion',
  // existential_meaning
  10: 'existential_meaning',
  13: 'existential_meaning',
  15: 'existential_meaning',
  16: 'existential_meaning',
  20: 'existential_meaning',
  // altruism
  1: 'altruism',
  4: 'altruism',
  6: 'altruism',
  9: 'altruism',
  17: 'altruism',
  18: 'altruism',
  // transcendence
  5: 'transcendence',
  12: 'transcendence',
  14: 'transcendence',
  19: 'transcendence',
}

const CAREER_CALLING_QUESTIONS: QuestionDef[] = [
  { number: 1, text: '我的工作或生涯能讓我有機會回饋社會', dimensionKey: 'altruism' },
  { number: 2, text: '我的工作或生涯能讓我樂在其中', dimensionKey: 'interest_passion' },
  { number: 3, text: '我的生涯是朝向自己喜歡做的事情來選擇或規劃', dimensionKey: 'interest_passion' },
  { number: 4, text: '我的工作或生涯對改變社會現狀有所幫助', dimensionKey: 'altruism' },
  { number: 5, text: '透過工作或生涯，讓我能達成來到這個世界的使命', dimensionKey: 'transcendence' },
  { number: 6, text: '我的工作或生涯可以對他人產生正面影響', dimensionKey: 'altruism' },
  { number: 7, text: '我的工作或生涯就是我的熱情所在', dimensionKey: 'interest_passion' },
  { number: 8, text: '我的工作或生涯能實踐自己的理想', dimensionKey: 'interest_passion' },
  { number: 9, text: '我的工作或生涯能夠為社會帶來正向的影響', dimensionKey: 'altruism' },
  { number: 10, text: '我的工作或生涯可以發揮我的潛能', dimensionKey: 'existential_meaning' },
  { number: 11, text: '我的工作或生涯讓我能朝自己所要的方向發展', dimensionKey: 'interest_passion' },
  { number: 12, text: '我知道要從事的工作或生涯方向，是因為領略到超越個人之外的呼喚力量', dimensionKey: 'transcendence' },
  { number: 13, text: '我的工作或生涯讓我能感受到存在的價值', dimensionKey: 'existential_meaning' },
  { number: 14, text: '我之所以從事目前的行業，是因為我相信這就是我的使命所在', dimensionKey: 'transcendence' },
  { number: 15, text: '我的工作或生涯可以發揮我的特質', dimensionKey: 'existential_meaning' },
  { number: 16, text: '我將我的生涯視為達成我生活目標的途徑', dimensionKey: 'existential_meaning' },
  { number: 17, text: '我的工作或生涯可以直接的幫助到別人', dimensionKey: 'altruism' },
  { number: 18, text: '我生涯中最重要的一部分，就是能夠幫助到別人', dimensionKey: 'altruism' },
  { number: 19, text: '我的工作或生涯能完成我這一生的使命', dimensionKey: 'transcendence' },
  { number: 20, text: '我的工作或生涯能做我覺得有意義的事', dimensionKey: 'existential_meaning' },
]

const OUTCOME_EXPECTATION_QUESTIONS: QuestionDef[] = [
  { number: 1, text: '成為護理人員，我將可以賺很多錢。', dimensionKey: 'security' },
  { number: 2, text: '成為護理人員，我將可以有許多工作機會。', dimensionKey: 'security' },
  { number: 3, text: '成為護理人員，我將可以獲得其他人的尊重。', dimensionKey: 'empowerment' },
  { number: 4, text: '成為護理人員，我將可以在人群中與眾不同。', dimensionKey: 'empowerment' },
  { number: 5, text: '成為護理人員，我將可以幫助他人。', dimensionKey: 'helping_others' },
  { number: 6, text: '成為護理人員，我將可以教導他人。', dimensionKey: 'helping_others' },
  { number: 7, text: '成為護理人員，我將可以隨時工作。', dimensionKey: 'flexibility' },
  { number: 8, text: '成為護理人員，我將可以與其他醫療專業人員一起工作。', dimensionKey: 'flexibility' },
  { number: 9, text: '成為護理人員，我將可以在不同的醫療環境中工作。', dimensionKey: 'opportunity' },
  { number: 10, text: '成為護理人員，我將可以追求其他護理專業生涯。', dimensionKey: 'opportunity' },
]

const BFI15_QUESTIONS: QuestionDef[] = [
  { number: 1, text: '充滿活力', dimensionKey: 'extraversion' },
  { number: 2, text: '有非常多熱情', dimensionKey: 'extraversion' },
  { number: 3, text: '喜歡外出、好社交的', dimensionKey: 'extraversion' },
  { number: 4, text: '樂於助人且無私的', dimensionKey: 'agreeableness' },
  { number: 5, text: '有寬容本質', dimensionKey: 'agreeableness' },
  { number: 6, text: '幾乎對所有人體貼且仁慈的', dimensionKey: 'agreeableness' },
  { number: 7, text: '可信賴的工作者', dimensionKey: 'conscientiousness' },
  { number: 8, text: '會堅持到工作完成', dimensionKey: 'conscientiousness' },
  { number: 9, text: '做事有效率的', dimensionKey: 'conscientiousness' },
  { number: 10, text: '擔心很多的', dimensionKey: 'neuroticism' },
  { number: 11, text: '情緒不穩定的', dimensionKey: 'neuroticism' },
  { number: 12, text: '容易感到緊張的', dimensionKey: 'neuroticism' },
  { number: 13, text: '具有生動想像力', dimensionKey: 'openness' },
  { number: 14, text: '有創造力的', dimensionKey: 'openness' },
  { number: 15, text: '重視藝術及美學經驗', dimensionKey: 'openness' },
]

export const SURVEY_QUESTIONS: Record<string, QuestionDef[]> = {
  career_calling: CAREER_CALLING_QUESTIONS,
  outcome_expectation: OUTCOME_EXPECTATION_QUESTIONS,
  bfi15: BFI15_QUESTIONS,
}

export const SURVEY_OPTION_LABELS: Record<string, string[]> = {
  career_calling: ['完全不符合', '部分符合', '大部分符合', '完全符合'],
  outcome_expectation: ['完全不重視', '有些不重視', '有些重視', '非常重視'],
  bfi15: ['非常不精確', '有些不精確', '普通', '有些精確', '非常精確'],
}

// Re-export for completeness — useful if callers need a quick lookup
export { CAREER_CALLING_DIM_MAP }
